<template>
	<AnalyticsPanel
		:state="state"
		:min-height="`${chartHeight}px`"
		:empty-title="emptyTitle"
		:empty-text="emptyText"
		@retry="$emit('retry')"
	>
		<template #header>
			<h2 class="mb-0 text-base font-semibold leading-6 text-surface-text-2">Trend</h2>
			<p class="mb-0 mt-0.5 text-xs leading-4 text-surface-7">
				{{ activeDefinition.trendLabel || activeDefinition.label }} per {{ intervalNoun }}<template v-if="compare"> with the previous period</template>
			</p>
		</template>
		<template v-if="zoomResetLabel" #actions>
			<!-- Escape hatch for drag-to-zoom: restores the range the user had before zooming. -->
			<button
				type="button"
				class="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md bg-surface-3 px-2.5 text-xs font-medium text-surface-text-1 ring-1 ring-inset ring-white/[0.06] hover:bg-surface-4 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-main/60"
				data-testid="analytics-trend-zoom-reset"
				@click="$emit('reset-zoom')"
			>
				<icon-base :width="12" :height="12" width-view-box="16" height-view-box="16" class="text-surface-8"><icon-chevron-left /></icon-base>
				{{ zoomResetLabel }}
			</button>
		</template>
		<template #toolbar>
			<!-- Same single-row scrolling strip as the Audience tabs (aligned beside it on desktop). -->
			<AnalyticsTabStrip
				:tabs="metricTabs"
				:active="metric"
				aria-label="Trend metric"
				testid="analytics-trend-tabs"
				@update:active="$emit('update:metric', $event)"
			/>
		</template>
		<template #skeleton>
			<div class="flex h-full flex-col justify-end gap-2" :style="{ minHeight: `${chartHeight}px` }">
				<div class="flex flex-1 items-end gap-1.5 px-2 pb-2">
					<div v-for="(h, i) in skeletonBars" :key="i" class="flex-1 animate-pulse rounded-t bg-surface-3/60" :style="{ height: `${h}%` }" />
				</div>
				<div class="h-3 w-full animate-pulse rounded bg-surface-3/40" />
			</div>
		</template>

		<div class="analytics-trend -mx-2" :class="{ 'analytics-trend--zoomable': zoomEnabled }" data-testid="analytics-trend" :data-zoomable="zoomEnabled ? 'true' : 'false'">
			<VueApexCharts
				:key="chartKey"
				type="line"
				:height="chartHeight"
				:options="options"
				:series="apexSeries"
				@zoomed="onZoomed"
			/>
		</div>
		<div v-if="compare" class="mt-1 flex items-center gap-4 px-1 text-[11px] leading-4 text-surface-7">
			<span class="inline-flex items-center gap-1.5"><span class="inline-block h-0.5 w-4 rounded bg-main-light" />Current period</span>
			<span class="inline-flex items-center gap-1.5"><span class="inline-block h-0 w-4 border-t-2 border-dashed border-surface-7/70" />Previous period</span>
		</div>
	</AnalyticsPanel>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import VueApexCharts from 'vue3-apexcharts';
import IconBase from '@/components/icon/IconBase.vue';
import IconChevronLeft from '@/components/icon/IconChevronLeft.vue';
import AnalyticsPanel from './AnalyticsPanel.vue';
import AnalyticsTabStrip from './AnalyticsTabStrip.vue';
import { METRICS, METRIC_BY_KEY } from './analyticsMetrics';
import { formatMetricValue, formatNumber } from './analyticsFormatters';
import { formatBucketLabel, tzOffsetMinutes } from './analyticsTimezone';
import { intervalMs } from './analyticsRange';
const props = defineProps({
	metric: { type: String, required: true },
	// [[timestampMs, value]] zero-filled on the query bucket grid.
	series: { type: Array, default: () => [] },
	// Same shape and length as `series`, for the previous window.
	previousSeries: { type: Array, default: null },
	compare: { type: Boolean, default: false },
	interval: { type: String, default: 'DAY' },
	timezone: { type: String, default: 'UTC' },
	state: { type: String, default: 'ready' },
	emptyTitle: { type: String, default: 'No analytics for this period' },
	emptyText: { type: String, default: 'No player activity was recorded for the selected content and dates. Try a different date range or selection.' },
	// Drag across the chart to select a time window (desktop / mouse only).
	zoomable: { type: Boolean, default: true },
	// When set, a "Back to …" chip appears in the header and emits `reset-zoom`.
	zoomResetLabel: { type: String, default: '' },
});

const emit = defineEmits(['update:metric', 'retry', 'zoom', 'reset-zoom']);

const metricTabs = METRICS.map((m) => ({ key: m.key, label: m.trendLabel || m.label }));
const activeDefinition = computed(() => METRIC_BY_KEY[props.metric] || METRICS[0]);
const intervalNoun = computed(() => ({ MINUTE: 'minute', HOUR: 'hour', DAY: 'day' }[props.interval] || 'day'));
const skeletonBars = [22, 35, 30, 48, 42, 60, 55, 70, 64, 78, 72, 85];

// 340px on desktop, 280px below lg (1024px).
const isDesktop = ref(true);
let mql = null;
function syncViewport() {
	isDesktop.value = mql ? mql.matches : true;
}
onMounted(() => {
	if (typeof window !== 'undefined' && window.matchMedia) {
		mql = window.matchMedia('(min-width: 1024px)');
		syncViewport();
		mql.addEventListener?.('change', syncViewport);
	}
});
onBeforeUnmount(() => mql?.removeEventListener?.('change', syncViewport));
const chartHeight = computed(() => (isDesktop.value ? 340 : 280));

const isDuration = computed(() => activeDefinition.value.unit === 'duration');

// Apex renders datetime axes in UTC; shifting each point by the zone offset
// makes UTC rendering read as local time in the selected zone. DAY buckets
// are UTC calendar days upstream, so they are left as-is.
function shift(ts) {
	if (props.interval === 'DAY') return ts;
	return ts + tzOffsetMinutes(props.timezone, new Date(ts)) * 60000;
}

const currentPoints = computed(() => props.series.map(([ts, v]) => ({ x: shift(ts), y: v, ts })));
const previousPoints = computed(() => {
	if (!props.compare || !Array.isArray(props.previousSeries) || !props.previousSeries.length) return [];
	// Overlay the previous window on the current x positions by index.
	return props.previousSeries.map(([ts, v], i) => {
		const anchor = currentPoints.value[i] || currentPoints.value[currentPoints.value.length - 1];
		return { x: anchor ? anchor.x : shift(ts), y: v, ts };
	});
});

// Mixed chart: the current period is a flat-filled area, the previous period
// a plain dashed line (Apex treats a 0 fill opacity as "use the default").
const apexSeries = computed(() => {
	const out = [{ name: 'Current period', type: 'area', data: currentPoints.value.map((p) => ({ x: p.x, y: p.y })) }];
	if (previousPoints.value.length) out.push({ name: 'Previous period', type: 'line', data: previousPoints.value.map((p) => ({ x: p.x, y: p.y })) });
	return out;
});

// Re-mount when the series count, metric or marker mode changes so Apex drops stale state.
const chartKey = computed(() => `${props.metric}-${apexSeries.value.length}-${chartHeight.value}-${showMarkers.value ? 'm' : 's'}-z${zoomEpoch.value}`);

// ------------------------------------------------------- drag-to-zoom
// Apex's x-zoom is used only as the selection gesture: the chosen window is
// emitted to the dashboard, which turns it into a custom date range and
// refetches everything. Apex's own visual zoom is thrown away on the next
// series update (re-mount via chartKey) so the refetched data is the only
// source of truth and nothing is zoomed twice.
// Touch devices keep native scrolling: Apex's touch handlers would hijack
// vertical swipes over the chart, so zoom is a >= lg mouse affordance.
const MIN_ZOOM_MS = 5 * 60 * 1000;
const MIN_ZOOM_BUCKETS = 2;
const zoomEpoch = ref(0);
let zoomPending = false;
const zoomEnabled = computed(() => props.zoomable && isDesktop.value);

function unshift(x) {
	if (props.interval === 'DAY') return x;
	return x - tzOffsetMinutes(props.timezone, new Date(x)) * 60000;
}

function onZoomed(chartContext, { xaxis } = {}) {
	if (!xaxis || !Number.isFinite(xaxis.min) || !Number.isFinite(xaxis.max)) return;
	const bucket = intervalMs(props.interval);
	// Snap to the bucket grid so the window covers whole readings.
	const from = Math.floor(unshift(xaxis.min) / bucket) * bucket;
	const to = Math.ceil(unshift(xaxis.max) / bucket) * bucket;
	const first = currentPoints.value[0]?.ts;
	const last = currentPoints.value[currentPoints.value.length - 1]?.ts;
	const lo = Number.isFinite(first) ? Math.max(from, first) : from;
	const hi = Number.isFinite(last) ? Math.min(to, last + bucket) : to;
	if (hi - lo < Math.max(MIN_ZOOM_MS, MIN_ZOOM_BUCKETS * bucket)) {
		zoomEpoch.value += 1; // too narrow: drop Apex's zoom, keep the view
		return;
	}
	zoomPending = true;
	emit('zoom', { from: new Date(lo), to: new Date(hi) });
}

// The dashboard answers a zoom with new series (possibly from cache, with no
// loading state in between); re-mount then so Apex forgets its own zoom.
watch(() => props.series, () => {
	if (!zoomPending) return;
	zoomPending = false;
	zoomEpoch.value += 1;
});

function yLabel(value) {
	if (isDuration.value) return formatMetricValue(props.metric, value);
	if (Math.abs(value) >= 1000) return formatMetricValue(props.metric, value);
	return formatNumber(value);
}

// Below ~60 buckets (DAY, most HOUR ranges) each point is a real reading, so
// draw straight segments with a marker per bucket; long MINUTE series would
// turn into a wall of dots, so they stay smooth and marker-less.
const MARKER_POINT_LIMIT = 60;
const showMarkers = computed(() => currentPoints.value.length <= MARKER_POINT_LIMIT);

function esc(s) {
	return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// "+81%" / "-12%" / "New" (previous was 0) / "0%"
function deltaLabel(current, previous) {
	if (!(previous > 0)) return current > 0 ? 'New' : '0%';
	const pct = Math.round(((current - previous) / previous) * 100);
	return `${pct > 0 ? '+' : ''}${pct}%`;
}

// Custom tooltip: explicit closing tags (an HTML `<span />` does not self-close)
// and inline-safe class hooks styled below.
function tooltipHtml({ dataPointIndex }) {
	const cur = currentPoints.value[dataPointIndex];
	if (!cur) return '';
	const prev = previousPoints.value[dataPointIndex];
	const label = activeDefinition.value.trendLabel || activeDefinition.value.label;
	const fmt = (v) => formatMetricValue(props.metric, v, { compact: false });
	let html = '<div class="analytics-trend-tip">';
	html += `<div class="analytics-trend-tip__date">${esc(formatBucketLabel(cur.ts, props.interval, props.timezone))}</div>`;
	html += '<div class="analytics-trend-tip__row">'
		+ '<span class="analytics-trend-tip__dot analytics-trend-tip__dot--cur"></span>'
		+ `<span class="analytics-trend-tip__name">${esc(label)}</span>`
		+ `<strong>${esc(fmt(cur.y))}</strong>`
		+ '</div>';
	if (prev) {
		html += '<div class="analytics-trend-tip__row analytics-trend-tip__row--prev">'
			+ '<span class="analytics-trend-tip__dot analytics-trend-tip__dot--prev"></span>'
			+ `<span class="analytics-trend-tip__name" title="${esc(formatBucketLabel(prev.ts, props.interval, props.timezone))}">Previous period</span>`
			+ `<strong>${esc(fmt(prev.y))}</strong>`
			+ `<em>${esc(deltaLabel(cur.y, prev.y))}</em>`
			+ '</div>';
	}
	html += '</div>';
	return html;
}

const xFormat = computed(() => {
	if (props.interval === 'MINUTE') return 'HH:mm';
	if (props.interval === 'HOUR') return 'dd MMM HH:mm';
	return 'dd MMM';
});

const options = computed(() => ({
	chart: {
		type: 'line',
		background: 'transparent',
		fontFamily: 'inherit',
		// Toolbar hidden (drag-only + our own reset chip). Apex only arms drag-zoom
		// when the (hidden) zoom tool is enabled and auto-selected.
		toolbar: { show: false, autoSelected: 'zoom', tools: { zoom: true, zoomin: false, zoomout: false, pan: false, reset: false, selection: false, download: false } },
		zoom: {
			enabled: zoomEnabled.value,
			type: 'x',
			autoScaleYaxis: true,
			zoomedArea: {
				fill: { color: '#2076FF', opacity: 0.16 },
				stroke: { color: '#2076FF', opacity: 0.6, width: 1 },
			},
		},
		// `zoomed` is bound on the component: vue3-apexcharts replaces chart.events with its own emitters.
		// No animations: Apex re-animates from the baseline on every resize,
		// which reads as "the data vanished" while a window is being resized.
		animations: { enabled: false },
		parentHeightOffset: 0,
		redrawOnParentResize: true,
	},
	colors: ['#2076FF', 'rgba(135, 148, 181, 0.65)'],
	stroke: { curve: showMarkers.value ? 'straight' : 'smooth', width: 2, dashArray: [0, 6], lineCap: 'round' },
	// Line series take their stroke alpha from fill.opacity, hence 1 for the
	// previous period (its colour already carries the 65% alpha).
	fill: { type: 'solid', opacity: [0.08, 1] },
	dataLabels: { enabled: false },
	legend: { show: false },
	markers: {
		size: showMarkers.value ? [3, 0] : 0,
		colors: ['#2076FF', 'rgba(135, 148, 181, 0.65)'],
		strokeColors: '#141E3D',
		strokeWidth: showMarkers.value ? 1.5 : 0,
		hover: { size: 6, sizeOffset: 0 },
	},
	grid: {
		borderColor: 'rgba(255, 255, 255, 0.12)',
		strokeDashArray: 0,
		xaxis: { lines: { show: false } },
		yaxis: { lines: { show: true } },
		padding: { left: 8, right: 12, top: 0, bottom: 0 },
	},
	xaxis: {
		type: 'datetime',
		tooltip: { enabled: false },
		// Vertical hover guide at the hovered bucket.
		crosshairs: {
			show: true,
			width: 1,
			position: 'back',
			stroke: { color: 'rgba(174, 186, 214, 0.45)', width: 1, dashArray: 4 },
			fill: { type: 'solid', color: 'transparent' },
		},
		axisBorder: { show: false },
		axisTicks: { show: false },
		tickAmount: isDesktop.value ? 8 : 4,
		labels: {
			datetimeUTC: true,
			format: xFormat.value,
			style: { colors: '#8794B5', fontSize: '12px', fontFamily: 'inherit' },
			offsetY: 2,
		},
	},
	yaxis: {
		min: 0,
		forceNiceScale: !isDuration.value,
		tickAmount: 4,
		labels: {
			style: { colors: '#8794B5', fontSize: '12px', fontFamily: 'inherit' },
			formatter: yLabel,
			offsetX: -6,
		},
	},
	tooltip: {
		shared: true,
		intersect: false,
		followCursor: false,
		theme: 'dark',
		custom: tooltipHtml,
	},
	states: { active: { filter: { type: 'none' } }, hover: { filter: { type: 'none' } } },
}));
</script>

<style>
.analytics-trend .apexcharts-tooltip {
	background: rgb(20, 30, 61) !important;
	border: 1px solid rgba(255, 255, 255, 0.08) !important;
	box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45) !important;
	border-radius: 8px !important;
	color: rgb(209, 219, 240);
	opacity: 0 !important;
	transition: opacity 0.12s ease;
}
/* StreamsDash/MonitoringChart.vue ships an unscoped `.apexcharts-tooltip { opacity: 0 !important }`
	that leaks into every Apex chart once loaded; win on specificity here. */
.analytics-trend .apexcharts-tooltip.apexcharts-active {
	opacity: 1 !important;
}
.analytics-trend-tip {
	display: flex;
	flex-direction: column;
	padding: 8px 10px;
	font-size: 12px;
	line-height: 16px;
	min-width: 200px;
}
.analytics-trend-tip__date {
	display: block;
	color: rgb(174, 186, 214);
	margin-bottom: 6px;
	font-weight: 500;
}
.analytics-trend-tip__row {
	display: flex;
	align-items: center;
	gap: 8px;
	color: rgb(209, 219, 240);
	white-space: nowrap;
}
.analytics-trend-tip__row + .analytics-trend-tip__row {
	margin-top: 4px;
}
.analytics-trend-tip__name {
	flex: 1 1 auto;
}
.analytics-trend-tip__row strong {
	margin-left: 12px;
	font-variant-numeric: tabular-nums;
	color: #fff;
	font-weight: 600;
}
.analytics-trend-tip__row--prev {
	color: rgb(135, 148, 181);
}
.analytics-trend-tip__row--prev strong {
	color: rgb(174, 186, 214);
	font-weight: 500;
}
.analytics-trend-tip__row em {
	font-style: normal;
	font-variant-numeric: tabular-nums;
	color: rgb(135, 148, 181);
	min-width: 3.5em;
	text-align: right;
}
.analytics-trend-tip__dot {
	display: inline-block;
	width: 8px;
	height: 8px;
	border-radius: 999px;
	flex-shrink: 0;
	box-sizing: border-box;
}
.analytics-trend-tip__dot--cur {
	background: #2076ff;
}
/* Hollow dashed marker to echo the dashed previous-period line. */
.analytics-trend-tip__dot--prev {
	background: transparent;
	border: 1.5px dashed rgba(135, 148, 181, 0.9);
}
/* Zoom is a drag gesture, so hint at it with the same cursor Apex uses for selection. */
.analytics-trend--zoomable .apexcharts-svg {
	cursor: crosshair;
}
.analytics-trend .apexcharts-marker {
	transition: none;
}
.analytics-trend .apexcharts-gridline {
	stroke: rgba(255, 255, 255, 0.12);
}
</style>
